import { SlideStyleSkeleton } from "@/components/skeletons/SlideStyleSkeleton";
import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { CANVAS_SIZE, nonDraggablesTypeSlide } from "@/lib/constants/slides";
import { useSlideStyle } from "@/lib/hooks/slides/editor/useSlideStyle";
import { useSlideStyleMutations } from "@/lib/hooks/slides/editor/useSlideStyleMutations";
import { withToast } from "@/lib/utils/withToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loadElementFromMyStyles } from "@/store/slideEditorSlice";
import type { SlideElement, SlideStyleType } from "@ai-teach/types";
import { Button, IconButton, LoadMoreButton, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { LuPaintbrush } from "react-icons/lu";
import { RiDeleteBinFill } from "react-icons/ri";
import { useInView } from "react-intersection-observer";
import { PanelTabs } from "../PanelTabs";
import { DisabledLockOverlay } from "./DisabledLockOverlay";
import { SaveSlideStyleModal } from "./SaveSlideStyleModal";

const TABS = [
  { id: "default" as const, label: "Default Styles" },
  { id: "personal" as const, label: "My Styles" },
];

export function MyStylesPanel() {
  const { setActivePanel } = useSlideEditorContext();
  const [activeTab, setActiveTab] = useState<SlideStyleType>("default");
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const dispatch = useAppDispatch();

  const slide = useAppSelector((state) => state.slideEditor.present.slide);

  const slideType = slide?.type;
  const knowledgeArea = slide?.knowledgeArea;

  const myStylesDisabled =
    knowledgeArea && slideType
      ? ((nonDraggablesTypeSlide[slideType] as readonly string[])?.includes(knowledgeArea) ?? false)
      : false;

  const {
    hasMore,
    loadMore,
    isLoading,
    items: slideStyles,
  } = useSlideStyle({
    type: activeTab,
    skip: myStylesDisabled,
  });
  const { deleteSlideStyle, deleteStyleLoading } = useSlideStyleMutations();

  const handleStyleClick = (elements: SlideElement[]) => {
    withToast(
      async () => {
        dispatch(loadElementFromMyStyles(elements));
      },
      {
        success: "Successfully loaded style",
        error: "Failed to load style",
        loading: "Loading...",
      },
      "load-slide-style-toast"
    );
    setActivePanel(null);
  };

  const filteredStyles = slideStyles?.filter(
    (style): style is NonNullable<typeof style> =>
      !!style && style.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (hasMore && inView && !isLoading) {
      loadMore();
    }
  }, [inView, hasMore, loadMore, isLoading]);

  return (
    <div className="px-2">
      {/* Section header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider px-1">My Styles</h3>
        <p className="text-xs text-slate-400 mt-1 px-1">Save and reuse slide arrangements</p>
      </div>

      {/* TABS */}
      <PanelTabs
        tabs={TABS}
        activeTab={activeTab}
        setActiveTab={setActiveTab as React.Dispatch<React.SetStateAction<string>>}
      />

      {/* Search bar */}
      <div
        className={`transition-all duration-300 ${myStylesDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""}`}
      >
        <SearchBar className="my-5" value={searchText} onChange={setSearchText} />
      </div>

      {/* Save button */}
      <div
        className={`w-full flex justify-end mb-4 transition-all duration-300 ${myStylesDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""}`}
      >
        <Button
          variant="primary"
          size="sm"
          disabled={myStylesDisabled}
          className="group relative overflow-hidden rounded-lg shadow-sm transition-all duration-200
    bg-gradient-to-r from-primary-500 to-accent-500 text-white
    hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
          onClick={() => setIsSaveModalOpen(true)}
        >
          <span className="relative">
            {activeTab === "default" ? "Save as Default Style" : "Save as Personal Style"}
          </span>
        </Button>
      </div>

      {/* Saved styles list */}
      <div
        className={`relative ${myStylesDisabled ? "min-h-[50vh] max-h-[50vh] overflow-hidden" : ""}`}
      >
        <div
          className={`grid grid-cols-2 gap-3 transition-all duration-300 ${myStylesDisabled ? "opacity-40 blur-[2px] pointer-events-none min-h-[50vh]" : ""}`}
        >
          {/* Loading State */}
          {isLoading && slideStyles?.length === 0 && <SlideStyleSkeleton count={6} />}

          {/* Empty State */}
          {!isLoading && (!slideStyles || slideStyles.length === 0) && (
            <div
              className={`col-span-full flex flex-col items-center justify-center ${myStylesDisabled ? "min-h-[50vh]" : "py-12"}`}
            >
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 border-2 border-slate-200 flex items-center justify-center mb-4">
                <LuPaintbrush fontSize={30} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-700 mb-1">No saved styles yet</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm">
                Click "Save Current Style" to save your first slide style
              </p>
            </div>
          )}

          {/* Render State */}
          {filteredStyles &&
            filteredStyles.length > 0 &&
            filteredStyles.map((style) => (
              <div
                key={style.id}
                onClick={() => style.elements && handleStyleClick(style.elements)}
                className="group relative border-2 border-slate-200 rounded-lg hover:border-primary-400 hover:-translate-y-1 hover:shadow-lg transition-all duration-200 cursor-pointer bg-white overflow-hidden"
              >
                {/* Thumbnail Image */}
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: CANVAS_SIZE.freeform.aspectRatio }}
                >
                  <img src={style.url} alt={style.name} className="w-full h-full" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>

                {/* Content */}
                <div className="flex flex-col items-center justify-center p-3 group-hover:scale-105 transition-transform duration-200">
                  <h4 className="font-medium text-slate-800 text-sm line-clamp-1">{style.name}</h4>
                  <p className="text-xs text-slate-500 mt-1">
                    {Array.isArray(style.elements) ? style.elements.length : 0} element
                    {Array.isArray(style.elements) && style.elements.length !== 1 ? "s" : ""}
                  </p>
                </div>

                {/* Delete Button */}
                <IconButton
                  variant="danger"
                  size="sm"
                  shape="square"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSlideStyle(style.id);
                  }}
                  disabled={deleteStyleLoading}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white text-error transition-opacity duration-200 hover:scale-110"
                >
                  <RiDeleteBinFill />
                </IconButton>
              </div>
            ))}
        </div>

        {/* Infinite scroll trigger */}
        {hasMore && (
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {isLoading && (
              <div className="flex items-center gap-2 text-muted">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>Loading more styles...</span>
              </div>
            )}
          </div>
        )}

        {myStylesDisabled && (
          <DisabledLockOverlay
            title="Styles Not Available"
            description="This slide type doesn't support custom styles"
          />
        )}
      </div>

      {/* Save modal */}
      <SaveSlideStyleModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        type={activeTab}
      />
    </div>
  );
}
